import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import { sortByCreatedAt } from "#modules/quoteRequestLinesV2/domain/buildQuoteRequestLineTableRows";
import { AssemblyWithChildren, sortQuoteRequestLines } from "shared";

export type AssemblyChildIdsByParentId = ReadonlyMap<string, string[]>;

export const buildAssemblyChildIdsByParentId = (
  quoteRequestLines: QuoteRequestLineDto[],
): AssemblyChildIdsByParentId => {
  const childIdsByParentId = new Map<string, string[]>();

  const collect = (assembly: AssemblyWithChildren<QuoteRequestLineDto>) => {
    if (assembly.children.length === 0) {
      return;
    }
    childIdsByParentId.set(
      assembly.quoteRequestLine.id,
      assembly.children.map((child) => child.quoteRequestLine.id),
    );
    assembly.children.forEach(collect);
  };

  const { batches } = sortQuoteRequestLines(quoteRequestLines);
  batches.forEach((assembliesInBatch) => {
    const mainAssembly = sortByCreatedAt(assembliesInBatch).at(0);
    if (mainAssembly) {
      collect(mainAssembly);
    }
  });

  return childIdsByParentId;
};

const collectDescendantIds = (
  id: string,
  childIdsByParentId: AssemblyChildIdsByParentId,
  descendantIds: string[] = [],
): string[] => {
  childIdsByParentId.get(id)?.forEach((childId) => {
    descendantIds.push(childId);
    collectDescendantIds(childId, childIdsByParentId, descendantIds);
  });
  return descendantIds;
};

// A parent is selected exactly when all of its children are, so enforcing the invariant
// bottom-up turns a half-selected assembly into the partial state the checkbox renders.
const applyParentInvariant = (
  id: string,
  childIdsByParentId: AssemblyChildIdsByParentId,
  selectedIds: Set<string>,
) => {
  const childIds = childIdsByParentId.get(id);
  if (!childIds) {
    return;
  }

  childIds.forEach((childId) => applyParentInvariant(childId, childIdsByParentId, selectedIds));

  if (childIds.every((childId) => selectedIds.has(childId))) {
    selectedIds.add(id);
  } else {
    selectedIds.delete(id);
  }
};

export const syncAssemblySelection = ({
  previousSelectedIds,
  nextSelectedIds,
  childIdsByParentId,
}: {
  previousSelectedIds: readonly string[];
  nextSelectedIds: readonly string[];
  childIdsByParentId: AssemblyChildIdsByParentId;
}): string[] => {
  const previous = new Set(previousSelectedIds);
  const next = new Set(nextSelectedIds);

  nextSelectedIds
    .filter((id) => !previous.has(id))
    .forEach((id) =>
      collectDescendantIds(id, childIdsByParentId).forEach((descendantId) =>
        next.add(descendantId),
      ),
    );

  previousSelectedIds
    .filter((id) => !next.has(id))
    .forEach((id) =>
      collectDescendantIds(id, childIdsByParentId).forEach((descendantId) =>
        next.delete(descendantId),
      ),
    );

  const childIds = new Set([...childIdsByParentId.values()].flat());
  [...childIdsByParentId.keys()]
    .filter((parentId) => !childIds.has(parentId))
    .forEach((rootId) => applyParentInvariant(rootId, childIdsByParentId, next));

  return [...next];
};

export const getIndeterminateRowIds = (
  selectedIds: readonly string[],
  childIdsByParentId: AssemblyChildIdsByParentId,
): ReadonlySet<string> => {
  const selected = new Set(selectedIds);

  return new Set(
    [...childIdsByParentId.keys()].filter(
      (parentId) =>
        !selected.has(parentId) &&
        collectDescendantIds(parentId, childIdsByParentId).some((descendantId) =>
          selected.has(descendantId),
        ),
    ),
  );
};
